import React, { useState, useEffect, useMemo } from "react";
import { GatsbyImage } from "gatsby-plugin-image";
import { Link } from "gatsby";
import Button from "../button/button";

const Hero = ({ type, content, backgroundImg, backgroundImgAlt, department, wordList, button, departmentText = "", headingIsH1, ctaButtonText, subHeader, ctaButtonLink, company, serviceTag = false, bannerClass, heroSettings }) => {

  const dynamicText = useMemo(() => {
    if(heroSettings && heroSettings?.heroDynamicTextRepeater) {
      return heroSettings?.heroDynamicTextRepeater;
    }else if(wordList) {
      return wordList;
    }else{
      return null;
    }
  }, [heroSettings, wordList]);

  const staticText = useMemo(() => {
    if(heroSettings && heroSettings?.heroStaticText) {
      return heroSettings?.heroStaticText;
    }else if(content) {
      return content;
    }else{
      return "";
    }
  }, [heroSettings, content]);

  const buttonLink = useMemo(() => {
    if(heroSettings && heroSettings?.heroCtaLink) {
      return heroSettings?.heroCtaLink;
    }else if(ctaButtonLink) {
      return ctaButtonLink;
    }else{
      return "/contact-us/";
    }
  }, [heroSettings, ctaButtonLink]);

  const buttonText = useMemo(() => {
    if(heroSettings && heroSettings?.heroCtaText) {
      return heroSettings?.heroCtaText;
    }else if(ctaButtonText) {
      return ctaButtonText;
    }else{
      return "Get in touch";
    }
  }, [heroSettings, ctaButtonText]);

  const heroTheme = useMemo(() => {
    if(heroSettings && heroSettings?.heroTheme) {
      return heroSettings?.heroTheme;
    }else if(type) {
      return type;
    }else{
      return "";
    }
  }, [heroSettings, type]);

  const initialWord = useMemo(() => {
    return dynamicText ? dynamicText[0].heroDynamicText || dynamicText[0] : "";
  }, [heroSettings]);
  
  const [currentWord, setCurrentWord] = useState(initialWord);
  const [listIndex, setListIndex] = useState(0);
  const [animateWord, setAnimateWord] = useState(true);
  const [delay, setDelay] = useState(true);

  let heroClass = '';
  let animateClass = '';
  let departmentClass = '';
  let preload = '';

  switch(staticText) {
    case 'we are ':
      preload = 'transparent.';
      break;
    case 'our flaunt values ':
      preload = 'empathy.';
      break;
    case 'The place for ':
      preload = 'industry news.';
      break;
    default:
      break;
  }

  useEffect(() => {
    const listId = setInterval(() => {
      setListIndex(i => (i + 1) % dynamicText?.length)
    }, 1000)
    return () => clearInterval(listId)
  }, [dynamicText])

  useEffect(() => {
    if (dynamicText?.length > 1) {
      setCurrentWord(dynamicText[listIndex].heroDynamicText)
    }else if(dynamicText && dynamicText[0].heroDynamicText) {
      setCurrentWord(dynamicText[0].heroDynamicText)
    }else if(dynamicText) {
      setCurrentWord(dynamicText[0])
    }
  }, [listIndex, dynamicText])

  const showDepartment = (department, departmentText, button) => {
    if (button && typeof button !== "undefined")
      return button;

    if (department && typeof department !== "undefined" && department !== "")
      return <span>{ departmentText }</span>

    return null;
  }

  switch(heroTheme) {
    case 'primary':
      heroClass = 'hero';
      switch(animateWord) {
        case true:
          animateClass = 'animate-word-change animate-word-change-in';
          break;
        case false:
          animateClass = 'animate-word-change';
          break;
        default :
          break;
      }
      break;
    case 'secondary':
      heroClass = 'hero hero-alt';
      switch(animateWord) {
        case true:
          animateClass = 'animate-word-change animate-word-change-alt animate-word-change-in animate-word-change-in-alt';
          break;
        case false:
          animateClass = 'animate-word-change animate-word-change-alt';
          break;
        default :
          break;
      }
      break;
    case 'post':
      switch(department) {
        case 'seo':
          departmentClass = 'seo';
          departmentText = departmentText ?? 'SEO';
          break;
        case 'development':
          departmentClass = 'development';
          departmentText = departmentText ?? 'Development';
          break;
        case 'paid-media':
          departmentClass = 'paid-media';
          departmentText = departmentText ?? 'Paid Media';
          break;
        case 'pr':
          departmentClass = 'pr';
          departmentText = departmentText ?? 'PR';
          break;
        case 'content-marketing':
          departmentClass = 'content-marketing';
          departmentText = departmentText ?? 'Content Marketing';
          break;
        case 'creative':
          departmentClass = 'creative';
          departmentText = departmentText ?? 'Creative';
          break;
        case 'default':
          departmentClass = 'default';
          break;
        case 'flaunt-digital':
        default:
          departmentClass = 'flaunt';
          departmentText = departmentText ?? 'Flaunt Brand Content';
        break;
      }
      (department !== null && typeof department !== 'undefined' && department !== '') ? heroClass = `hero hero-post hero-post-${departmentClass}` : heroClass = 'hero hero-post';
      break;
    case "half":
      heroClass = 'hero half-height-hero';
      break;
    case "service":
      heroClass = `hero service-hero service-background-${department} service-colour-${department}`;
      break;
    default:
      heroClass = 'hero';
      break;
  }

  return (
    <div 
      className={ heroClass }
      >
      { backgroundImg !== null && typeof backgroundImg !== 'undefined' && backgroundImg !== '' ? 
        <GatsbyImage image={ backgroundImg } className="lazyload" alt={ typeof backgroundImgAlt !== 'undefined' && backgroundImgAlt.length > 0 ? backgroundImgAlt : "" } />
      : 
        null 
      }
      <div className={`hero-banner ${bannerClass}`}>
        { company ?
          <p className="hero-company">
            { company }
          </p>
        : 
          null 
        }
        <Heading 
          h1={ typeof headingIsH1 !== 'undefined' && headingIsH1 === true }
        >
          <div>
            { staticText }
          </div>
          { dynamicText ?
            <div className={ animateClass } >
              <span className='animate-word-change-word'>{ dynamicText.length > 1 ?
                ( dynamicText ? currentWord : preload )
              :
                currentWord
              }</span>
            </div>
          : 
            null
          }
        </Heading>
          { showDepartment(department, departmentText, button) }
          { subHeader && !department && !departmentText ?
            <p className="hero-banner-subheading">
              { subHeader }
            </p>
          : 
            null
          }
          { serviceTag ?
            <div className={ `service-background-light-${department} custom-case-study-service-tag` }>
              { serviceTag }
            </div>
          :
            null
          }
      </div>
      <Button
        to={buttonLink}
        title={buttonText}
        children={buttonText}
        type={"nav-contact"}
      />
    </div>
    
  )

}

const Heading = ({ h1, children }) => {
  if (h1)
    return <h1 className="h1">{ children }</h1>
  else
    return <h2 className="h1">{children}</h2>
}

export default Hero;